/**
 * EshopGeoGenerator.jsx
 * ------------------------------------------------------------------
 * Mass GEO landing-page generator UI. Mounted as a tab below the
 * `EshopScanReport` inside `EshopScannerPage` once a report exists.
 *
 * 4-step flow:
 *   1. Pick target URLs from the scan report (multi-select with
 *      "select all products / categories" presets).
 *   2. Pick language + cities (preset for language + free-form add).
 *   3. Pick template types (Product / Category / FAQ — can stack).
 *   4. Hit Generate → backend runs sync (≤4 pages) or async + polling.
 *
 * After the job completes, the result table renders via the bundled
 * `EshopGeoPagesList`. ZIP-download + per-page preview live there.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Loader2, ShoppingCart, Folder, FileQuestion, MapPin,
  ChevronRight, AlertCircle, Sparkles, Plus, X, Globe,
  CheckCircle, Download,
} from "lucide-react";
import EshopGeoPagesList from "./EshopGeoPagesList";

const TEMPLATE_META = [
  { key: "product",  icon: ShoppingCart,  label: "Product × City",  blurb: "Buyer-intent landing per produkt × mesto" },
  { key: "category", icon: Folder,        label: "Category × City", blurb: "Kategória s lokálnym kontextom" },
  { key: "faq",      icon: FileQuestion,  label: "FAQ / Guide",     blurb: "Q&A optimalizované pre AI engines" },
];

export default function EshopGeoGenerator({ report, userEmail }) {
  const [preset, setPreset] = useState(null);
  const [lang, setLang] = useState("sk");
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState(["product"]);
  const [extraCities, setExtraCities] = useState([]);
  const [extraCityInput, setExtraCityInput] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);

  // Load the 26-locale preset catalogue once on mount.
  useEffect(() => {
    axios.get(`${API}/eshop-geo/cities-preset`)
      .then(r => setPreset(r.data))
      .catch(() => setPreset({ languages: [], cities: {} }));
  }, []);

  // Poll the job doc every 2s while it's running. Stop when status
  // is `done` or `failed` — at which point the FE can render the page
  // list. We don't want polling forever if the user navigates away,
  // so cancel via the cleanup function.
  useEffect(() => {
    if (!job || (job.status !== "running" && job.status !== "queued")) return;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/eshop-geo/job/${job.id}`, {
          headers: { "X-User-Email": userEmail },
        });
        if (!cancelled) setJob(r.data);
      } catch {}
    }, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [job?.id, job?.status, userEmail]);

  const presetCities = preset?.cities?.[lang] || [];
  const allCities = useMemo(() => {
    // Dedupe by (name, country) — overrides come first so user's
    // facts win over our preset facts.
    const seen = new Set();
    const out = [];
    for (const c of [...extraCities, ...presetCities]) {
      const key = `${(c.name || "").toLowerCase()}|${(c.country || "").toUpperCase()}`;
      if (key && !seen.has(key)) { seen.add(key); out.push(c); }
    }
    return out;
  }, [extraCities, presetCities]);

  const totalPages = selectedTargets.length * allCities.length * selectedTemplates.length;

  // Group scan results by type so the target picker offers
  // "select all products / categories" shortcuts.
  const groupedTargets = useMemo(() => {
    const groups = { product: [], category: [], blog: [], page: [] };
    for (const r of (report?.results || [])) {
      const t = r.type || "page";
      (groups[t] || groups.page).push(r);
    }
    return groups;
  }, [report]);

  const toggleTarget = (url) => {
    setSelectedTargets(prev =>
      prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  const selectAllOfType = (type) => {
    const urls = (groupedTargets[type] || []).map(r => r.url);
    setSelectedTargets(prev => {
      const set = new Set(prev);
      urls.forEach(u => set.add(u));
      return Array.from(set);
    });
  };

  const toggleTemplate = (key) => {
    setSelectedTemplates(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const addExtraCity = () => {
    const txt = extraCityInput.trim();
    if (!txt) return;
    // Accept "Name, Country" or just "Name".
    const [name, country] = txt.split(",").map(s => s.trim());
    setExtraCities(prev => [...prev, {
      name, country: (country || "SK").toUpperCase(), facts: "",
    }]);
    setExtraCityInput("");
  };

  const removeExtraCity = (idx) => {
    setExtraCities(prev => prev.filter((_, i) => i !== idx));
  };

  const handleGenerate = async () => {
    if (totalPages === 0) return;
    setSubmitting(true); setError(null); setJob(null);
    try {
      const r = await axios.post(`${API}/eshop-geo/generate`, {
        report_id: report.id,
        target_urls: selectedTargets,
        template_types: selectedTemplates,
        cities: extraCities,  // backend merges with preset[lang]
        lang,
        auto_publish: autoPublish,
      }, {
        headers: { "X-User-Email": userEmail },
        timeout: 180000,
      });
      setJob(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        setError({ type: "quota", payload: d });
      } else if (typeof d === "object") {
        setError({ type: "validation", message: d.message || JSON.stringify(d) });
      } else {
        setError({ type: "generic", message: typeof d === "string" ? d : "Generation failed" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="eshop-geo-generator">
      <header className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-purple-400" />
        <div>
          <div className="text-xs uppercase tracking-widest text-purple-400">Mass Page Generator</div>
          <div className="text-lg font-bold">Vygeneruj lokálne SEO stránky pre celý sken</div>
        </div>
      </header>

      {/* Step 1 — target picker */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="eshop-geo-step-targets">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-sm font-bold">
            1. Vyber URL zo scanu ({selectedTargets.length} vybratých)
          </h3>
          <div className="flex flex-wrap gap-2 text-xs">
            {["product", "category", "blog", "page"].filter(t => (groupedTargets[t] || []).length).map(t => (
              <button
                key={t}
                onClick={() => selectAllOfType(t)}
                data-testid={`eshop-geo-select-all-${t}`}
                className="px-2 py-1 bg-purple-900/40 hover:bg-purple-900/60 text-purple-300 border border-purple-700/40 rounded-sm"
              >
                + všetky {t} ({groupedTargets[t].length})
              </button>
            ))}
            {selectedTargets.length > 0 && (
              <button
                onClick={() => setSelectedTargets([])}
                className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-sm"
              >
                Zrušiť výber
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[260px] overflow-y-auto border border-gray-900 rounded-sm">
          {(report?.results || []).slice(0, 200).map((r, idx) => (
            <label
              key={r.url + idx}
              className="flex items-center gap-3 px-3 py-1.5 hover:bg-black/40 cursor-pointer border-b border-gray-900"
            >
              <input
                type="checkbox"
                checked={selectedTargets.includes(r.url)}
                onChange={() => toggleTarget(r.url)}
                data-testid={`eshop-geo-target-checkbox-${idx}`}
                className="accent-purple-500"
              />
              <span className="text-[10px] uppercase text-gray-500 w-16 shrink-0">{r.type || "page"}</span>
              <span className="text-xs text-purple-300 truncate flex-1" title={r.url}>
                {r.url.replace(/^https?:\/\//, "")}
              </span>
              <span className="text-[10px] text-gray-500 shrink-0">{r.grade || "F"}</span>
            </label>
          ))}
        </div>
      </section>

      {/* Step 2 — language + cities */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="eshop-geo-step-cities">
        <h3 className="text-sm font-bold mb-3">2. Jazyk + mestá ({allCities.length} celkom)</h3>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-xs text-gray-400 inline-flex items-center gap-2">
            <Globe className="w-3.5 h-3.5" />
            Jazyk:
            <select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              data-testid="eshop-geo-lang-select"
              className="ml-1 px-2 py-1 bg-black/50 border border-gray-800 text-gray-200 rounded-sm focus:outline-none focus:border-purple-500"
            >
              {(preset?.languages || []).map(l => (
                <option key={l} value={l}>{l.toUpperCase()}</option>
              ))}
            </select>
          </label>
          <span className="text-[10px] text-gray-500">
            (preset {presetCities.length} miest · pridáš ďalšie nižšie)
          </span>
        </div>

        {presetCities.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {presetCities.map((c) => (
              <span key={c.name + c.country} className="px-2 py-1 text-xs bg-gray-900 border border-gray-800 text-gray-300 rounded-sm">
                <MapPin className="w-3 h-3 inline mr-1 text-gray-500" />
                {c.name} ({c.country})
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={extraCityInput}
            onChange={(e) => setExtraCityInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtraCity(); } }}
            placeholder="Pridať mesto (napr. „Trnava, SK“)"
            data-testid="eshop-geo-city-input"
            className="flex-1 px-3 py-2 bg-black/50 border border-gray-800 focus:border-purple-500 rounded-sm text-sm text-white placeholder-gray-600 focus:outline-none"
          />
          <button
            onClick={addExtraCity}
            data-testid="eshop-geo-city-add-btn"
            className="px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-sm flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> Pridať
          </button>
        </div>

        {extraCities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {extraCities.map((c, i) => (
              <span key={i} className="px-2 py-1 text-xs bg-purple-900/30 border border-purple-700/40 text-purple-200 rounded-sm inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {c.name} ({c.country})
                <button onClick={() => removeExtraCity(i)} className="ml-1 hover:text-white">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Step 3 — templates */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="eshop-geo-step-templates">
        <h3 className="text-sm font-bold mb-3">3. Typy stránok ({selectedTemplates.length} vybraté)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TEMPLATE_META.map(t => {
            const Icon = t.icon;
            const active = selectedTemplates.includes(t.key);
            return (
              <button
                key={t.key}
                onClick={() => toggleTemplate(t.key)}
                data-testid={`eshop-geo-template-${t.key}`}
                className={`p-3 text-left rounded-sm border transition-colors ${
                  active
                    ? "bg-purple-900/30 border-purple-500 text-purple-100"
                    : "bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${active ? "text-purple-300" : "text-gray-500"}`} />
                  <span className="font-bold text-sm">{t.label}</span>
                  {active && <CheckCircle className="w-3.5 h-3.5 text-purple-400 ml-auto" />}
                </div>
                <div className="text-xs text-gray-400">{t.blurb}</div>
              </button>
            );
          })}
        </div>

        <label className="mt-4 flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={autoPublish}
            onChange={(e) => setAutoPublish(e.target.checked)}
            data-testid="eshop-geo-autopublish-toggle"
            className="accent-purple-500"
          />
          Auto-publikovať na <span className="text-purple-300">toprankpulse.com/api/geo-page/&lt;slug&gt;</span> (užívatelia odtiaľ pôjdu na tvoj e-shop)
        </label>
      </section>

      {/* Step 4 — submit */}
      <section className="p-5 bg-gradient-to-br from-purple-900/30 to-black border border-purple-700/40 rounded-sm" data-testid="eshop-geo-step-submit">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-purple-400 mb-1">Spolu stránok</div>
            <div className="text-3xl font-black tabular-nums text-white" data-testid="eshop-geo-total-pages">
              {totalPages.toLocaleString("sk-SK")}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              {selectedTargets.length} URL × {allCities.length} miest × {selectedTemplates.length} šablón
              · 1 stránka = 1 quota credit
            </div>
          </div>
          <button
            onClick={handleGenerate}
            disabled={submitting || totalPages === 0}
            data-testid="eshop-geo-generate-btn"
            className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black uppercase tracking-wider rounded-sm flex items-center gap-2"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Spúšťam…</>
              : <><Sparkles className="w-4 h-4" /> Vygenerovať {totalPages} stránok <ChevronRight className="w-4 h-4" /></>
            }
          </button>
        </div>
      </section>

      {error?.type === "quota" && (
        <div className="p-4 bg-rose-900/20 border border-rose-700/40 rounded-sm" data-testid="eshop-geo-quota-error">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-rose-400 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-rose-200">Kvóta na GEO pages je nedostatočná</div>
              <div className="text-gray-300 mt-1">
                Požadovaných {totalPages} pages, limit {error.payload?.limit ?? "?"} · {error.payload?.current ?? 0} už použitých tento mesiac.
              </div>
              <a href="/pricing#eshop" className="inline-flex items-center gap-1 mt-2 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-sm">
                Upgrade plánu <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      )}

      {error?.type === "validation" && (
        <div className="p-3 bg-amber-900/20 border border-amber-700/40 rounded-sm text-sm text-amber-200" data-testid="eshop-geo-validation-error">
          {error.message}
        </div>
      )}

      {error?.type === "generic" && (
        <div className="p-3 bg-rose-900/20 border border-rose-700/40 rounded-sm text-sm text-rose-200" data-testid="eshop-geo-generic-error">
          {error.message}
        </div>
      )}

      {/* Job progress + results */}
      {job && (
        <section className="p-5 bg-black/40 border border-gray-800 rounded-sm" data-testid="eshop-geo-job-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {job.status === "done" ? (
                <CheckCircle className="w-4 h-4 text-emerald-400" />
              ) : job.status === "failed" ? (
                <AlertCircle className="w-4 h-4 text-rose-400" />
              ) : (
                <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
              )}
              <span className="text-sm font-bold" data-testid="eshop-geo-job-status">
                {job.status === "done"
                  ? `Hotové · ${job.generated} stránok vygenerovaných${job.failed ? `, ${job.failed} zlyhalo` : ""}`
                  : job.status === "failed"
                    ? `Job zlyhal: ${job.error || "neznáma chyba"}`
                    : `Generujem… ${job.generated} / ${job.total}`
                }
              </span>
            </div>

            {job.status === "done" && job.generated > 0 && (
              <a
                href={`${API}/eshop-geo/export/${job.id}.zip`}
                data-testid="eshop-geo-zip-download"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded-sm"
                onClick={(e) => {
                  // Pre signed URL flow we'd POST first — but auth is
                  // header-based, so the browser's GET with cookies +
                  // X-User-Email isn't possible. Fetch as blob and
                  // download manually so the headers go through.
                  e.preventDefault();
                  axios.get(`${API}/eshop-geo/export/${job.id}.zip`, {
                    headers: { "X-User-Email": userEmail },
                    responseType: "blob",
                  }).then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `toprankpulse-geo-pages-${new Date().toISOString().slice(0, 10)}.zip`;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  });
                }}
              >
                <Download className="w-3.5 h-3.5" /> Stiahnuť ZIP
              </a>
            )}
          </div>

          <div className="h-2 bg-gray-900 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                job.status === "done" ? "bg-emerald-500"
                : job.status === "failed" ? "bg-rose-500"
                : "bg-purple-500"
              }`}
              style={{ width: `${job.total ? Math.max(2, (job.generated / job.total) * 100) : 2}%` }}
            />
          </div>

          {job.status === "done" && job.generated > 0 && (
            <div className="mt-5">
              <EshopGeoPagesList jobId={job.id} userEmail={userEmail} autoPublish={job.auto_publish} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
